import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { CalendarDays, MapPin, Phone, ChevronRight, UserCircle, Building2, Package, AlertTriangle, PackageCheck, Boxes } from "lucide-react";

const dayKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const orderDayKey = (o) => (o.delivery_date_confirmed || "").slice(0, 10);

const dayLabel = (key, todayKey, tomorrowKey) => {
  if (key === todayKey) return "Aujourd'hui";
  if (key === tomorrowKey) return "Demain";
  const d = new Date(`${key}T12:00:00`);
  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function DeliveryCard({ o }) {
  const rec = o.recipient || {};
  return (
    <div data-testid={`agenda-card-${o.id}`} className="lc-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="font-mono text-[11px] text-[var(--lc-gray)]">{o.ref}</span>
          <StatusBadge status={o.status} />
        </div>
        <Link to={`/admin/orders/${o.id}`} data-testid={`agenda-open-${o.id}`} className="p-2 -m-1 rounded-md hover:bg-black/5 shrink-0">
          <ChevronRight className="w-4 h-4 text-[var(--lc-gray)]" />
        </Link>
      </div>

      <a
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.arrival_address || "")}`}
        target="_blank"
        rel="noreferrer"
        data-testid={`agenda-maps-${o.id}`}
        className="flex items-start gap-2 font-semibold text-sm leading-snug hover:text-[var(--lc-orange)] transition-colors"
      >
        <MapPin className="w-4 h-4 mt-0.5 text-[var(--lc-orange)] shrink-0" />
        <span>{o.arrival_address}</span>
      </a>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-[var(--lc-dark-gray)]">
        {o.company_name && (
          <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-[var(--lc-gray)]" />{o.company_name}</span>
        )}
        {o.vendor_name && (
          <span className="inline-flex items-center gap-1"><UserCircle className="w-3 h-3 text-[var(--lc-gray)]" />{o.vendor_name}</span>
        )}
        <span className="inline-flex items-center gap-1">
          <Package className="w-3 h-3 text-[var(--lc-gray)]" />
          {o.total_packages || 0} colis · {o.oversized_count || 0} HG
        </span>
        <span className="inline-flex items-center gap-1">
          {o.kitchen_premounted ? <PackageCheck className="w-3 h-3 text-[var(--lc-gray)]" /> : <Boxes className="w-3 h-3 text-[var(--lc-gray)]" />}
          {o.kitchen_premounted ? "Prémontée" : "Non prémontée"}
        </span>
      </div>

      {(rec.first_name || rec.phone) && (
        <div className="flex items-center justify-between gap-2 bg-[var(--lc-surface)] rounded-md px-3 py-2">
          <span className="text-xs font-semibold truncate">{rec.first_name} {rec.last_name}</span>
          {rec.phone && (
            <a
              href={`tel:${rec.phone}`}
              data-testid={`agenda-call-${o.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--lc-orange)] bg-[var(--lc-orange-light)] rounded-full px-3 py-1.5 shrink-0"
            >
              <Phone className="w-3 h-3" /> {rec.phone}
            </a>
          )}
        </div>
      )}

      {o.kitchen_paid_full === false && (
        <div className="flex items-start gap-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid={`agenda-unpaid-${o.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Cuisine non réglée à 100% — vérifier avant l'enlèvement
        </div>
      )}
    </div>
  );
}

export default function AdminAgenda() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("all");

  useEffect(() => {
    api.get("/admin/agenda").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  const today = new Date();
  const todayKey = dayKey(today);
  const tomorrowKey = dayKey(new Date(today.getTime() + 86400000));

  const days = useMemo(() => {
    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date(today.getTime() + i * 86400000);
      return { key: dayKey(d), d };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countByDay = useMemo(() => {
    const m = {};
    orders.forEach((o) => {
      const k = orderDayKey(o);
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [orders]);

  const late = orders.filter((o) => orderDayKey(o) < todayKey);
  const upcoming = orders.filter((o) => orderDayKey(o) >= todayKey);

  const groups = useMemo(() => {
    const m = new Map();
    upcoming.forEach((o) => {
      const k = orderDayKey(o);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(o);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [upcoming]);

  const shownGroups = selected === "all" ? groups : groups.filter(([k]) => k === selected);
  const shownLate = selected === "all" ? late : [];

  return (
    <div data-testid="admin-agenda-page" className="max-w-2xl mx-auto">
      <div className="mb-4">
        <div className="lc-small-label mb-1">Planning</div>
        <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
          <CalendarDays className="w-7 h-7 text-[var(--lc-orange)]" /> Agenda
        </h1>
        <p className="text-sm text-[var(--lc-gray)] mt-1">{orders.length} livraison{orders.length > 1 ? "s" : ""} à venir</p>
      </div>

      {/* Horizontal day strip */}
      <div className="sticky top-14 z-20 -mx-4 sm:mx-0 bg-[var(--lc-surface)] py-2">
        <div className="flex gap-2 overflow-x-auto px-4 sm:px-0 pb-1" style={{ scrollbarWidth: "none" }} data-testid="agenda-day-strip">
          <button
            onClick={() => setSelected("all")}
            data-testid="agenda-day-all"
            className={`shrink-0 px-4 py-2 rounded-xl border text-xs font-bold transition-colors ${
              selected === "all"
                ? "bg-[var(--lc-black)] text-white border-[var(--lc-black)]"
                : "bg-white border-[var(--lc-border)] text-[var(--lc-dark-gray)]"
            }`}
          >
            Tout
            {late.length > 0 && <span className="ml-1.5 text-red-500">({late.length} en retard)</span>}
          </button>
          {days.map(({ key, d }) => {
            const n = countByDay[key] || 0;
            const active = selected === key;
            return (
              <button
                key={key}
                onClick={() => setSelected(active ? "all" : key)}
                data-testid={`agenda-day-${key}`}
                className={`shrink-0 w-14 py-2 rounded-xl border flex flex-col items-center transition-colors relative ${
                  active
                    ? "bg-[var(--lc-orange)] text-white border-[var(--lc-orange)]"
                    : key === todayKey
                    ? "bg-[var(--lc-orange-light)] border-[var(--lc-orange)]/40 text-[var(--lc-black)]"
                    : "bg-white border-[var(--lc-border)] text-[var(--lc-dark-gray)]"
                }`}
              >
                <span className={`text-[10px] uppercase font-bold ${active ? "text-white/80" : "text-[var(--lc-gray)]"}`}>
                  {d.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "")}
                </span>
                <span className="text-base font-black leading-tight">{d.getDate()}</span>
                {n > 0 ? (
                  <span className={`text-[10px] font-black rounded-full min-w-[16px] px-1 ${active ? "bg-white text-[var(--lc-orange)]" : "bg-[var(--lc-orange)] text-white"}`}>
                    {n}
                  </span>
                ) : (
                  <span className="text-[10px] text-transparent">·</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-sm text-[var(--lc-gray)] mt-4">Chargement…</div>}

      {!loading && shownLate.length === 0 && shownGroups.length === 0 && (
        <div className="lc-card p-10 text-center mt-4" data-testid="agenda-empty">
          <CalendarDays className="w-8 h-8 mx-auto text-[var(--lc-gray)] mb-2" />
          <div className="text-base font-semibold mb-1">Aucune livraison prévue</div>
          <p className="text-sm text-[var(--lc-gray)]">
            {selected === "all" ? "Les livraisons confirmées apparaîtront ici." : "Rien ce jour-là. Touchez à nouveau le jour pour tout afficher."}
          </p>
        </div>
      )}

      {shownLate.length > 0 && (
        <div className="mt-4" data-testid="agenda-late-section">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <h2 className="text-sm font-black uppercase tracking-wider text-red-600">En retard</h2>
          </div>
          <div className="space-y-3">
            {shownLate.map((o) => <DeliveryCard key={o.id} o={o} />)}
          </div>
        </div>
      )}

      {shownGroups.map(([key, list]) => (
        <div key={key} className="mt-5" data-testid={`agenda-group-${key}`}>
          <h2 className={`text-sm font-black uppercase tracking-wider mb-2 ${key === todayKey ? "text-[var(--lc-orange)]" : "text-[var(--lc-dark-gray)]"}`}>
            {dayLabel(key, todayKey, tomorrowKey)}
            <span className="ml-2 text-[var(--lc-gray)] font-bold">({list.length})</span>
          </h2>
          <div className="space-y-3">
            {list.map((o) => <DeliveryCard key={o.id} o={o} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
